<template>
  <div
    class="console"
    :class="{
      'with-completions': parsedInput && parsedInput.showsSuggestionList,
      'with-footer': selectedType && isGenericMode,
    }"
    @click.stop="focus()"
    @focus="$emit('focus')"
  >
    <div class="console-input">
      <ConsoleTypeBar
        v-if="selectedType && isGenericMode"
        :type="selectedType"
        :type-label="selectedTypeLabel"
        @select-type="onSelectTypePill"
      />
      <ParsedInput
        ref="parsedInput"
        v-model:focused="focused"
        :placeholder="placeholder"
        :context="context"
        :provided-input-value="providedInputValue"
        :is-redirection-stopped="isRedirectionStopped"
        :display-actions="!(selectedType && isGenericMode)"
        class="parsed-input"
        @blur="$emit('blur')"
        @close="$emit('close')"
        @escape="onEscape"
        @completion-selected="
          (completion) => $emit('completionSelected', completion)
        "
        @clear-context="$emit('clearContext')"
        @submit="onSubmit"
        @type-change="onTypeChange"
        @mandate-change="onMandateChange"
      >
        <template #belowInput>
          <ConsolePills
            v-if="
              selectedType &&
              isGenericMode &&
              (selectedProperties.length > 0 || pillContext)
            "
            :type="selectedType"
            :properties="selectedProperties"
            :values="pillValues"
            :context="pillContext"
            @select-property="onSelectPropertyPill"
            @clear-property="onClearPropertyPill"
            @update-select="onUpdateSelectPill"
            @update-relation="onUpdateRelationPill"
            @update-bool="onUpdateBoolPill"
            @update-text="onUpdateTextPill"
            @update-number="onUpdateNumberPill"
            @update-notes="onUpdateNotesPill"
          />
        </template>
      </ParsedInput>
      <!-- Footer bar replacing the action suggestion rows in the create
           flow: the destination on the left, Capture/Create on the right.
           Create is always the primary action (Enter / Mod+Enter); the
           secondary Capture button (Shift+Enter, Mod+Shift+Enter to also
           open) shows on macOS whenever a context is attached — gated
           captures open the upgrade/plugin modal instead. -->
      <ConsoleFooterBar
        v-if="selectedType && isGenericMode"
        :type="selectedType"
        :type-label="selectedTypeLabel"
        :destination="destination"
        :show-destination="showDestination"
        :can-add="!!(parsedInput && parsedInput.canParse)"
        :can-capture="!!(parsedInput && parsedInput.canCapture)"
        :capture="!!pillContext"
        :context="pillContext"
        :capture-availability="captureAvailability"
        @select-destination="onSelectDestinationPill"
        @clear-destination="onClearDestinationPill"
        @add="submit($event)"
        @capture="submit($event, true)"
      />
    </div>
    <!-- Type switcher popup, anchored on the type pill. Lives inside the
         console so its clicks stop at the console root instead of reaching
         window-level handlers (e.g. the dashboard's close-on-background). -->
    <TypeDatabasePicker
      ref="typePickerEl"
      :selected-ids="selectedTypePickerId ? [selectedTypePickerId] : []"
      writable-only
    />
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch,
  type Component,
} from "vue";

import type {
  GraphDatabase,
  GraphResult,
  Mandate,
  MandateDates,
  MandateDestination,
  RoutineMandateCompletion,
} from "@ctrl/controller-rpc";

import {
  captureGate,
  CONTEXTUAL_CAPTURE_ENTITLEMENT,
  CONTEXTUAL_CAPTURE_FEATURE_NAME,
  CONTEXTUAL_CAPTURE_PLUGIN_ID,
  useCaptureAvailability,
} from "@/components/console/capture";
import {
  ConsoleDefaultValues,
  DEFAULT_EVENT_DURATION_MINUTES,
  DEFAULT_PILL,
  DEFAULT_TYPE_LABELS,
  DefaultConsoleKind,
  defaultConsoleKindOf,
  defaultTypePillProperties,
} from "@/components/console/default";
import ConsoleFooterBar, {
  type PillDestination,
} from "@/components/ConsoleFooterBar.vue";
import ConsolePills, {
  type PillValue,
  type PropertyValue,
} from "@/components/ConsolePills.vue";
import ConsoleTypeBar from "@/components/ConsoleTypeBar.vue";
import {
  getTypeIcon,
  isConsoleCreatableCustomType,
} from "@/components/databases";
import { createBlockParagraph } from "@/components/document";
import { findContextField } from "@/components/graph/context";
import type { GraphRelationResult } from "@/components/graph/graph-relation";
import {
  introspectType,
  TypePropertyInfo,
} from "@/components/graph/introspection";
import { toGraphDocument } from "@/components/graph/properties/document-serialization";
import {
  parseGraphRelationResult,
  parseGraphRelationResults,
} from "@/components/graph/result";
import {
  extractTitleFromGraphResult,
  getObjectTitle,
} from "@/components/graph/title";
import { dateToGraphResult } from "@/components/graph/utils";
import EventIcon from "@/components/icons/EventIcon.vue";
import MoveToIcon from "@/components/icons/MoveToIcon.vue";
import PageIcon from "@/components/icons/PageIcon.vue";
import PersonIcon from "@/components/icons/PersonIcon.vue";
import TaskIcon from "@/components/icons/TaskIcon.vue";
import * as inject from "@/components/inject";
import { openPluginModal } from "@/components/modals/plugin";
import { openUpgradeModal } from "@/components/modals/upgrade";
import { State as ParsedInputState } from "@/components/parsed-input";
import ParsedInput, { Query } from "@/components/ParsedInput.vue";
import TypeDatabasePicker from "@/components/TypeDatabasePicker.vue";
import { defaultTypePickerId } from "@/composables/useTypeDatabasePicker";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import type { Context } from "@/types";
import { in_mac } from "@/utils/in";
import { isTypeMovable } from "@/utils/move";

// The mandate `dates` field is a temporal *span* variant, not a list. Reduce it
// to the single concrete day a date property can hold (start of the span);
// spans without a meaningful first day set nothing.
const mandateFirstDate = (dates: MandateDates): Date | null => {
  if (dates.type === "date") return dates.date;
  if (dates.type === "date_range") return dates.start;
  return null;
};

/**
 * `GraphRelation` emits its committed value as a broad `GraphResult`, but at
 * runtime it is the relation record(s) its own `data` prop expects. Narrow at
 * this boundary (empty selections come through as `null`/`[]`).
 */
const asRelationData = (
  value: GraphResult
): GraphRelationResult | GraphRelationResult[] | undefined => {
  if (value === null || value === undefined) return undefined;
  if (Array.isArray(value)) {
    const relations = parseGraphRelationResults(value);
    return relations && relations.length > 0 ? relations : undefined;
  }
  return parseGraphRelationResult(value);
};

export default defineComponent({
  components: {
    ConsoleFooterBar,
    ConsolePills,
    ConsoleTypeBar,
    ParsedInput,
    TypeDatabasePicker,
  },
  props: {
    context: { type: Object as PropType<Context> },
    isRedirectionStopped: { type: Boolean, default: false },
    modal: { type: Boolean, default: false },
    providedInputValue: { type: String, required: false },
  },
  emits: {
    blur: () => true,
    close: () => true,
    completionSelected: (_completion: RoutineMandateCompletion["value"]) =>
      true,
    submit: (_: Query) => true,
    clearContext: () => true,
    focus: () => true,
  },
  setup(props, { emit }) {
    const releaser = useReleaser();
    const focused = ref(false);
    const parsedInput = ref<InstanceType<typeof ParsedInput>>();
    const logger = inject.logger("console");
    const controller = inject.controller();
    const profile = inject.profile();
    const isContextualCaptureEnabled = inject.isContextualCaptureEnabled();
    // For the contextual-capture gate: a capture action submitted without the
    // entitlement (or with the plugin disabled) opens a modal instead.
    const billing = inject.billing();
    const captureAvailability = useCaptureAvailability(
      billing,
      isContextualCaptureEnabled
    );
    const router = inject.router();
    // The attached capture context, surfaced as a pill when the selected
    // type can store it (the parsed input then offers a capture variant of
    // the primary action). The footer's secondary Capture button shows in
    // lockstep; the primary Create button always submits without the
    // context. Contextual capture is macOS-only: elsewhere neither the pill
    // nor the Capture button show.
    const pillContext = computed<Context | null>(() =>
      in_mac() && props.context && parsedInput.value?.captureAction
        ? props.context
        : null
    );
    // Shared DatePicker and DestinationPicker provided by the app shell
    // (Routine in the main window, the console window's App.vue in the
    // dashboard).
    const datePickerEl = inject.datePicker();
    const destinationPickerEl = inject.destinationPicker();
    logger.debug("datePicker injected", { present: !!datePickerEl });
    // Currently selected type and its properties. The type/destination bar
    // renders above the input; property pills render above the suggestion list.
    // User-defined types are introspected from their
    // schema; the default types (Task/Event/Page) get a curated pill set with
    // their extensions database backing Labels/Priority. With no `as <type>`
    // the console creates a Task, so the Task pills show by default.
    const selectedType = ref<GraphDatabase | null>(null);
    const selectedProperties = ref<TypePropertyInfo[]>([]);
    // Set when the pills describe a default type (task/event/page); null for
    // user-defined types.
    const selectedDefaultKind = ref<DefaultConsoleKind | null>(null);
    // The default types' extensions databases (also standing in for the types
    // themselves in the pills/type picker), live off the profile so the pills
    // follow schema edits: a property added to a default type in Settings
    // shows up without a reload.
    const defaultDatabases = computed(
      () => profile.value.user.databases.default
    );
    // Writable calendar names, feeding the event Calendar pill's choices.
    const calendarNames = ref<string[]>([]);
    void (async () => {
      const calendars = (await controller.calendar.all()).release() ?? [];
      calendarNames.value = calendars
        .filter((calendar) => calendar.isWritable && !calendar.disabled)
        .map((calendar) => calendar.summary);
    })();
    // The console's parsing mode, tracked from `fill`. Pills only apply to the
    // create flow (generic mode) — not to schedule/postpone/search sub-modes.
    const currentMode = ref<string | undefined>(undefined);
    const isGenericMode = computed<boolean>(
      () => !currentMode.value || currentMode.value === "generic"
    );
    // Values picked for the current type's properties, keyed by property id.
    // Saved here as the user fills them in (e.g. via the DatePicker) so they can
    // be applied to the object once it is created. Reset whenever the selected
    // type changes.
    const propertyValues = ref<Record<string, PropertyValue>>({});
    // Latest parsed mandate from the input, used to map its keywords (currently
    // `on <date>`) onto the selected type's properties as the user types.
    const mandate = ref<Mandate | null>(null);
    const onMandateChange = (next: Mandate | null) => {
      mandate.value = next;
    };
    // Name + type icon for the parsed `>` redirection destination, shown as a
    // read-only pill next to the type. The icon reflects the destination
    // object's kind (task/page/event/person) or a custom object's emoji; the
    // label is its title. Resolved from the destination reference on change.
    const resolveDestination = async (
      destination: MandateDestination
    ): Promise<{ label: string; icon: Component | string }> => {
      if (destination.type === "calendar")
        return { label: "Calendar", icon: MoveToIcon };
      const { kind, id } = destination.id;
      switch (kind) {
        case "object": {
          const db = (await controller.graph.database.ofObject(id)).release();
          const icon = getTypeIcon(profile.value, db);
          const title =
            (await getObjectTitle(controller, id)) || db?.data_name || "Object";
          return { label: title, icon };
        }
        case "page":
          return {
            label: (await controller.page.get(id)).release()?.title || "Page",
            icon: PageIcon,
          };
        case "task":
          return {
            label: (await controller.task.get(id)).release()?.title || "Task",
            icon: TaskIcon,
          };
        case "task-rec":
          return {
            label:
              (await controller.taskRecurrent.get(id)).release()?.title ||
              "Task",
            icon: TaskIcon,
          };
        case "event":
          return {
            label: (await controller.event.get(id)).release()?.title || "Event",
            icon: EventIcon,
          };
        case "event-rec":
          return {
            label:
              (await controller.event.recurrent.get(id)).release()?.title ||
              "Event",
            icon: EventIcon,
          };
        case "people": {
          const people = (await controller.people.get(id)).release();
          return {
            label: people?.name?.display || people?.email || "Person",
            icon: PersonIcon,
          };
        }
      }
      return { label: "Destination", icon: MoveToIcon };
    };
    const destinationLabel = ref<string | null>(null);
    const destinationIcon = ref<Component | string | null>(null);
    // Destination chosen through the pill's DestinationPicker. Only used when
    // the input has no parsed `>` destination — the parser always wins.
    const pickedDestination = ref<MandateDestination | null>(null);
    // Whether the shown destination comes from the parsed `>` redirection; the
    // pill is then read-only (change it by editing the input, not the picker).
    const destinationParserFilled = computed<boolean>(
      () => !!mandate.value?.destination
    );
    // The destination pill's content, `null` while nothing is set (the pill
    // then shows its placeholder).
    const destination = computed<PillDestination | null>(() =>
      destinationLabel.value && destinationIcon.value
        ? {
            label: destinationLabel.value,
            icon: destinationIcon.value,
            parserFilled: destinationParserFilled.value,
          }
        : null
    );
    const effectiveDestination = computed<MandateDestination | null>(
      () => mandate.value?.destination ?? pickedDestination.value
    );
    // Whether the selected type supports a destination at all: an object can
    // only be created inside another object when its type is movable
    // (isTypeMovable) — among the default kinds only Task, plus the movable
    // custom types. Events and pages cannot be re-parented, so they get no
    // destination pill.
    const showDestination = computed<boolean>(
      () => !selectedType.value || isTypeMovable(selectedType.value)
    );
    // Guards against out-of-order async resolutions clobbering a newer value.
    let destinationToken = 0;
    watch(
      effectiveDestination,
      async (destination) => {
        const token = ++destinationToken;
        if (!destination) {
          destinationLabel.value = null;
          destinationIcon.value = null;
          return;
        }
        const { label, icon } = await resolveDestination(destination);
        if (token === destinationToken) {
          destinationLabel.value = label;
          destinationIcon.value = icon;
        }
      },
      { immediate: true }
    );
    // Clicking the destination pill opens the shared DestinationPicker anchored
    // on it (re-clicking toggles it closed). Only reachable when no `>`
    // destination is parsed — the pill is read-only otherwise.
    const onSelectDestinationPill = (element?: HTMLElement) => {
      const picker = destinationPickerEl.value;
      const workspaceId = selectedType.value?.workspace;
      if (!picker || !workspaceId) return;
      if (picker.isDisplayed) {
        picker.closePicker();
        return;
      }
      void picker.showPicker(
        element,
        { workspaceId, placeholder: "Set destination…" },
        (destination) => {
          if (!destination) return;
          pickedDestination.value = destination;
          logger.debug("destination picked", { type: destination.type });
        },
        "left"
      );
    };
    // Clicking the cross on a picked destination unsets it.
    const onClearDestinationPill = () => {
      pickedDestination.value = null;
    };
    // The type resolved by the parser (`as <type>`), or null when the input
    // has none — the console then creates a Task, the default type.
    const parsedType = ref<GraphDatabase | null>(null);
    // The parser hands `as <type>` databases as static snapshots; mirror the
    // selected custom type with a live schema subscription (re-bound on each
    // type change, released with the console) so custom-type pills follow
    // schema edits the same way the default types' do.
    const liveParsedType = ref<GraphDatabase | null>(null);
    void releaser
      .add(
        bindAsync(parsedType, async (database) => {
          if (!database || !isConsoleCreatableCustomType(database))
            return releasableRef<GraphDatabase | null>(null);
          return controller.graph.database.get(database.workspace, database.id);
        })
      )
      .then((binding) => {
        releaser.defer(
          watch(
            binding,
            (database) => {
              liveParsedType.value = database ?? null;
            },
            { immediate: true }
          )
        );
      });
    // Derive the pills from the parsed type: introspection for custom types
    // (user-defined or prepackaged like Label), the curated default pill set
    // for task/event/page (including the no-type default Task). Non-creatable
    // built-ins (Person, ...) get none.
    const applySelectedType = () => {
      const parsed = parsedType.value;
      // Prefer the live subscription's schema over the parser's static
      // snapshot — guarded by id, because on a type change the live ref
      // still holds the previous type until its re-bind lands.
      const database =
        liveParsedType.value && liveParsedType.value.id === parsed?.id
          ? liveParsedType.value
          : parsed;
      if (database && isConsoleCreatableCustomType(database)) {
        selectedDefaultKind.value = null;
        selectedType.value = database;
        // The title field is captured by the console's input, so it is not
        // shown as a pill; virtual fields are controller-added read-only
        // mirrors that cannot be set at creation; the Context property is
        // filled by contextual capture, never by the user.
        const contextField = findContextField(database);
        selectedProperties.value = introspectType(database).filter(
          (property) =>
            !property.title &&
            !property.virtual &&
            property.name !== contextField
        );
        return;
      }
      const kind = database ? defaultConsoleKindOf(database) : "task";
      if (!kind) {
        selectedDefaultKind.value = null;
        selectedType.value = null;
        selectedProperties.value = [];
        return;
      }
      // Prefer the enriched extensions database from the profile
      // over the parser's `as <type>` database: the parser serializes the
      // raw stored table (no virtual Title/Notes mirrors, no title metadata),
      // so introspecting it would mistake the first real column (Labels) for
      // the title field and drop its pill.
      const extensionsDb =
        defaultDatabases.value[kind] ??
        (database && defaultConsoleKindOf(database) ? database : null) ??
        null;
      selectedDefaultKind.value = kind;
      selectedType.value = extensionsDb;
      selectedProperties.value = extensionsDb
        ? defaultTypePillProperties(kind, extensionsDb, calendarNames.value)
        : [];
    };
    const onTypeChange = (database: GraphDatabase | null) => {
      propertyValues.value = {};
      pickedDestination.value = null;
      parsedType.value = database;
      applySelectedType();
      logger.debug("type selected", {
        type: database ? database.data_name || database.name : "(default task)",
        properties: selectedProperties.value.length,
      });
    };
    // Re-derive the pills once the async sources (extension databases,
    // calendars) resolve and whenever a live schema subscription updates,
    // without dropping values the user already set.
    watch([defaultDatabases, calendarNames, liveParsedType], () =>
      applySelectedType()
    );
    // Initial state: no parsed type yet, i.e. the default Task pills.
    applySelectedType();
    // Display name for the type pill. The default types' extensions databases
    // stand in for the types themselves, so their user-facing names win over
    // the database name.
    const selectedTypeLabel = computed<string | null>(() =>
      selectedDefaultKind.value
        ? DEFAULT_TYPE_LABELS[selectedDefaultKind.value]
        : null
    );
    // Values derived from the parsed input keywords, keyed by property id.
    // `on <date>` maps onto the type's first Date property; for default types
    // the label/priority/duration/participant keywords also map onto their
    // pills. These update live as the user types; a type with no matching
    // property yields nothing (the keyword stays highlighted in the input but
    // sets no value).
    const keywordValues = computed<Record<string, PropertyValue>>(() => {
      const values: Record<string, PropertyValue> = {};
      const current = mandate.value;
      if (!current) return values;
      const dates = current.dates;
      if (dates) {
        const dateProperty = selectedProperties.value.find(
          (property) => property.type === "Date"
        );
        if (dateProperty) {
          // A parsed week (e.g. "next week") postpones the task, and only the
          // task "Planned for" pill can hold a week; for other Date
          // properties (and other spans) fall back to the span's first day.
          if (
            dates.type === "week" &&
            dateProperty.id === DEFAULT_PILL.plannedFor
          ) {
            values[dateProperty.id] = { type: "Week", week: dates.week };
          } else {
            const date = mandateFirstDate(dates);
            if (date) values[dateProperty.id] = { type: "Date", date };
          }
        }
      }
      if (selectedDefaultKind.value) {
        const byName = (name: string) =>
          selectedProperties.value.find((property) => property.name === name);
        if (current.labels && current.labels.length > 0) {
          const labelsProperty = byName("Labels");
          // Labels is a relation: pass the parsed id/name references through
          // so the pill displays the target objects like a UI-picked value
          // (ConsolePills synthesizes the records). Keep the plain-names
          // fallback for a non-relation property matched by name.
          if (labelsProperty)
            values[labelsProperty.id] =
              labelsProperty.type === "Relation"
                ? { type: "RelationRefs", refs: current.labels }
                : {
                    type: "Select",
                    value: current.labels.map((label) => label.name),
                  };
        }
        if (current.priority) {
          const priorityProperty = byName("Priority");
          if (priorityProperty)
            values[priorityProperty.id] = {
              type: "Select",
              value: current.priority.name,
            };
        }
        if (current.duration !== undefined) {
          // Mandate durations are in seconds; the pill shows minutes.
          values[DEFAULT_PILL.duration] = {
            type: "Number",
            value: Math.round(current.duration / 60),
          };
        }
        if (current.participants && current.participants.length > 0) {
          values[DEFAULT_PILL.participants] = {
            type: "Text",
            value: current.participants
              .map((participant) =>
                typeof participant === "string"
                  ? participant
                  : participant.email
              )
              .join(", "),
          };
        }
      }
      return values;
    });
    // Summary of the default calendar, shown on the event Calendar pill when
    // the user hasn't picked one — the calendar creation will actually use.
    const defaultCalendarName = ref<string | null>(null);
    void (async () => {
      const calendar = (await controller.calendar.defaultCal()).release();
      defaultCalendarName.value = calendar?.summary ?? null;
    })();
    // The values an event gets when its pills are left untouched, surfaced on
    // the pills so the user sees what will be created: today, the default
    // duration, the default calendar, attending, busy. These mirror the
    // creation-time fallbacks in `console.ts` and are not submitted — creation
    // applies them on its own.
    const defaultPillValues = computed<Record<string, PropertyValue>>(() => {
      if (selectedDefaultKind.value !== "event") return {};
      const values: Record<string, PropertyValue> = {
        [DEFAULT_PILL.date]: { type: "Date", date: new Date() },
        [DEFAULT_PILL.duration]: {
          type: "Number",
          value: DEFAULT_EVENT_DURATION_MINUTES,
        },
        [DEFAULT_PILL.active]: { type: "Boolean", value: true },
        [DEFAULT_PILL.busy]: { type: "Boolean", value: true },
      };
      if (defaultCalendarName.value)
        values[DEFAULT_PILL.calendar] = {
          type: "Select",
          value: defaultCalendarName.value,
        };
      return values;
    });
    // Effective values shown on the pills and applied on create: the type's
    // default values as a base, then keyword-derived values, overridden by any
    // value the user set explicitly through a pill.
    const effectiveValues = computed<Record<string, PropertyValue>>(() => ({
      ...defaultPillValues.value,
      ...keywordValues.value,
      ...propertyValues.value,
    }));
    // The pills' values with their provenance, the single map ConsolePills
    // derives all its display state from. The source mirrors the
    // `effectiveValues` precedence: an explicit pill edit wins over a parser
    // keyword, which wins over a type default.
    const pillValues = computed<Record<string, PillValue>>(() => {
      const values: Record<string, PillValue> = {};
      for (const [id, value] of Object.entries(effectiveValues.value)) {
        const source =
          id in propertyValues.value
            ? "explicit"
            : id in keywordValues.value
              ? "parser"
              : "default";
        values[id] = { value, source };
      }
      return values;
    });
    // Type switcher popup, anchored on the type pill.
    const typePickerEl = ref<InstanceType<typeof TypeDatabasePicker> | null>(
      null
    );
    // Picker entry id of the current type, so the switcher ticks it: default
    // kinds use their synthetic entry id, custom types their database id.
    // The console always has an effective type (the default Task when the
    // input names none), except when the parser resolved a non-creatable
    // built-in — nothing is ticked then.
    const selectedTypePickerId = computed<string | null>(() => {
      if (selectedDefaultKind.value)
        return defaultTypePickerId(selectedDefaultKind.value);
      return selectedType.value?.id ?? null;
    });
    // Clicking the type pill lists the available types; picking one rewrites
    // the input's `as <Type>` clause so the parser drives the switch
    // (re-clicking toggles the popup closed).
    const onSelectTypePill = (_type: GraphDatabase, element?: HTMLElement) => {
      const picker = typePickerEl.value;
      if (!picker) return;
      if (picker.isDisplayed) {
        picker.closePicker();
        return;
      }
      void picker.showPicker(element, (database) => {
        if (!database) return;
        const kind = defaultConsoleKindOf(database);
        const asName = kind
          ? DEFAULT_TYPE_LABELS[kind]
          : database.data_name || database.name;
        void parsedInput.value?.setType(database, asName);
        logger.debug("type picked", { type: asName });
        parsedInput.value?.focus();
      });
    };
    // Clicking a property pill opens a popup above it to edit that property.
    // Date properties open the DatePicker; other property types are wired later.
    const onSelectPropertyPill = (
      property: TypePropertyInfo,
      element?: HTMLElement
    ) => {
      logger.debug("property pill clicked", {
        name: property.name,
        type: property.type,
        hasDatePicker: !!datePickerEl.value,
        hasElement: !!element,
      });
      if (property.type === "Date") {
        // Re-clicking the same pill toggles the popup closed.
        if (datePickerEl.value?.isDisplayed) {
          datePickerEl.value.closePicker();
          return;
        }
        // The task "Planned for" pill also offers picking a week: a day
        // schedules the task, a week postpones it to that batch.
        const withWeeks = property.id === DEFAULT_PILL.plannedFor;
        // Pass the pill's current value (if any): the picker highlights it,
        // and only offers "Clear" when there is a value to clear.
        const current = propertyValues.value[property.id];
        const scheduling =
          current?.type === "Date"
            ? current.date
            : current?.type === "Week"
              ? current.week
              : null;
        void datePickerEl.value?.showPicker(
          element,
          scheduling,
          (selection) => {
            // "Clear" unsets the pill value.
            if (selection === null) {
              onClearPropertyPill(property);
              return;
            }
            if (selection.mode === "date") {
              propertyValues.value = {
                ...propertyValues.value,
                [property.id]: { type: "Date", date: selection.date },
              };
              logger.debug("property value set", {
                name: property.name,
                date: selection.date.toISOString(),
              });
            } else if (selection.mode === "week") {
              propertyValues.value = {
                ...propertyValues.value,
                [property.id]: { type: "Week", week: selection.week },
              };
              logger.debug("property value set", {
                name: property.name,
                week: selection.week,
              });
            }
          },
          "left",
          {
            modes: withWeeks ? ["date", "week"] : ["date"],
            placement: "top",
          }
        );
      }
    };
    // Clicking the cross on a set pill unsets its value.
    const onClearPropertyPill = (property: TypePropertyInfo) => {
      if (!(property.id in propertyValues.value)) return;
      const next = { ...propertyValues.value };
      delete next[property.id];
      propertyValues.value = next;
      logger.debug("property value cleared", { name: property.name });
    };
    // A Select/multi-select editor committed a new value. Store it (or clear
    // when emptied) so it can be applied when the object is created.
    const onUpdateSelectPill = (
      property: TypePropertyInfo,
      value: GraphResult
    ) => {
      const normalized: string | string[] | null =
        typeof value === "string"
          ? value
          : Array.isArray(value)
            ? value.filter((v): v is string => typeof v === "string")
            : null;
      if (
        normalized === null ||
        (Array.isArray(normalized) && normalized.length === 0)
      ) {
        onClearPropertyPill(property);
        return;
      }
      propertyValues.value = {
        ...propertyValues.value,
        [property.id]: { type: "Select", value: normalized },
      };
      logger.debug("property value set", {
        name: property.name,
        value: normalized,
      });
    };
    // A relation/multi-relation editor committed a new value. Store the record(s)
    // (or clear when emptied) so they can be applied when the object is created.
    const onUpdateRelationPill = (
      property: TypePropertyInfo,
      value: GraphResult
    ) => {
      const relation = asRelationData(value);
      if (relation === undefined) {
        onClearPropertyPill(property);
        return;
      }
      const label = Array.isArray(relation)
        ? `${relation.length} selected`
        : (extractTitleFromGraphResult(relation) ?? "1 selected");
      propertyValues.value = {
        ...propertyValues.value,
        [property.id]: { type: "Relation", value: relation, label },
      };
      logger.debug("relation value set", { name: property.name, label });
    };
    // A boolean editor toggled its value. Store it so it can be applied when the
    // object is created.
    const onUpdateBoolPill = (
      property: TypePropertyInfo,
      value: GraphResult
    ) => {
      const bool = value === true;
      propertyValues.value = {
        ...propertyValues.value,
        [property.id]: { type: "Boolean", value: bool },
      };
      logger.debug("bool value set", { name: property.name, value: bool });
    };
    // A text editor committed a new value. Store it, or clear when emptied so
    // an empty pill collapses back to its name.
    const onUpdateTextPill = (
      property: TypePropertyInfo,
      value: GraphResult
    ) => {
      const text = typeof value === "string" ? value : "";
      if (text === "") {
        onClearPropertyPill(property);
        return;
      }
      propertyValues.value = {
        ...propertyValues.value,
        [property.id]: { type: "Text", value: text },
      };
      logger.debug("text value set", { name: property.name });
    };
    // A number editor committed a new value. GraphNumber only emits a numeric
    // value; store it so it can be applied when the object is created (use the
    // pill's cross to clear).
    const onUpdateNumberPill = (
      property: TypePropertyInfo,
      value: GraphResult
    ) => {
      if (value === null) {
        onClearPropertyPill(property);
        return;
      }
      if (typeof value !== "number") return;
      propertyValues.value = {
        ...propertyValues.value,
        [property.id]: { type: "Number", value },
      };
      logger.debug("number value set", { name: property.name, value });
    };
    // The Notes text area changed. Clear when emptied; otherwise store the raw
    // text (turned into a document on create).
    const onUpdateNotesPill = (property: TypePropertyInfo, text: string) => {
      if (text.trim() === "") {
        onClearPropertyPill(property);
        return;
      }
      propertyValues.value = {
        ...propertyValues.value,
        [property.id]: { type: "Notes", text },
      };
      logger.debug("notes value set", { name: property.name });
    };
    // A pill's draft value as the graph value the property-set queries expect.
    const propertyValueToGraphResult = (value: PropertyValue): GraphResult => {
      switch (value.type) {
        case "Date":
          return dateToGraphResult(value.date);
        case "Week":
          // Unreachable: week values only exist on the synthetic task
          // "Planned for" pill, which is applied through the typed task
          // defaults, never through graph properties.
          return null;
        case "Notes":
          return toGraphDocument({
            blocks: value.text
              .split("\n")
              .map((line) => createBlockParagraph(line)),
          });
        case "RelationRefs":
          // Unreachable: relation refs come from parsed keywords (`#label`),
          // which only exist for default kinds — those reach creation through
          // the mandate, never through graph properties.
          return null;
        default:
          return value.value;
      }
    };
    // Collected property values keyed by property *name* (what the graph
    // `rows add properties` query expects), built from the pills' draft values.
    const collectedProperties = (): Record<string, GraphResult> => {
      const byId = new Map(selectedProperties.value.map((p) => [p.id, p]));
      const properties: Record<string, GraphResult> = {};
      for (const [id, value] of Object.entries(effectiveValues.value)) {
        const property = byId.get(id);
        if (!property) continue;
        properties[property.name] = propertyValueToGraphResult(value);
      }
      return properties;
    };
    // Pill values for a default type, translated into the typed payload
    // `performAction` applies through the task/event/page RPCs. Only explicit
    // pill edits are collected: keyword-driven values (dates, labels,
    // priority, participants, ...) already reach creation through the parsed
    // action/mandate.
    const collectedDefaults = (): ConsoleDefaultValues | null => {
      const kind = selectedDefaultKind.value;
      if (!kind) return null;
      const byId = new Map(selectedProperties.value.map((p) => [p.id, p]));
      const defaults: ConsoleDefaultValues = { kind };
      for (const [id, value] of Object.entries(propertyValues.value)) {
        const property = byId.get(id);
        if (!property) continue;
        switch (id) {
          case DEFAULT_PILL.plannedFor:
            // A day schedules the task, a week postpones it (both flow into
            // the task's `scheduled` field, which takes either).
            if (value.type === "Date") defaults.plannedFor = value.date;
            else if (value.type === "Week") defaults.plannedFor = value.week;
            break;
          case DEFAULT_PILL.date:
            if (value.type === "Date") defaults.date = value.date;
            break;
          case DEFAULT_PILL.duration:
            if (value.type === "Number") defaults.durationMinutes = value.value;
            break;
          case DEFAULT_PILL.allDay:
            if (value.type === "Boolean") defaults.allDay = value.value;
            break;
          case DEFAULT_PILL.starred:
            if (value.type === "Boolean") defaults.starred = value.value;
            break;
          case DEFAULT_PILL.notes:
            if (value.type === "Notes") defaults.notes = value.text;
            break;
          case DEFAULT_PILL.description:
            if (value.type === "Notes") defaults.description = value.text;
            break;
          case DEFAULT_PILL.location:
            if (value.type === "Text") defaults.location = value.value;
            break;
          case DEFAULT_PILL.calendar:
            if (value.type === "Select" && typeof value.value === "string")
              defaults.calendar = value.value;
            break;
          case DEFAULT_PILL.active:
            if (value.type === "Boolean") defaults.active = value.value;
            break;
          case DEFAULT_PILL.busy:
            if (value.type === "Boolean") defaults.busy = value.value;
            break;
          case DEFAULT_PILL.participants:
            if (value.type === "Text")
              defaults.participants = value.value
                .split(/[\s,;]+/)
                .filter((email) => email.length > 0);
            break;
          default:
            // Extensions-backed pills keep their real column ids; their
            // values are applied to the extensions row as graph properties,
            // keyed by name like custom-type properties.
            defaults.extensions = {
              ...defaults.extensions,
              [property.name]: propertyValueToGraphResult(value),
            };
        }
      }
      // Only `kind` means no pill contributed anything.
      return Object.keys(defaults).length > 1 ? defaults : null;
    };
    // Attach the pill property values to create queries before forwarding the
    // submit so they can be applied when the object is created. Keyword-derived
    // values (e.g. `on <date>`) are already folded into `collectedProperties`
    // via `effectiveValues`. A destination picked through the pill (only
    // possible when the input has no parsed `>` destination) is folded into the
    // query's mandate, where consumers already read the parser's destination.
    const onSubmit = (query: Query) => {
      if (query.kind === "create") {
        // A gated capture action can't be performed: the user is either not
        // entitled to contextual capture (upgrade) or hasn't enabled the
        // plugin. Open the matching modal instead of creating anything; the
        // console stays open behind it.
        const gate = captureGate(query.action, captureAvailability.value);
        if (gate === "upgrade") {
          openUpgradeModal(billing, undefined, CONTEXTUAL_CAPTURE_ENTITLEMENT);
          return;
        }
        if (gate === "plugin") {
          openPluginModal(router, {
            pluginId: CONTEXTUAL_CAPTURE_PLUGIN_ID,
            featureName: CONTEXTUAL_CAPTURE_FEATURE_NAME,
          });
          return;
        }
        let create = query;
        if (
          pickedDestination.value &&
          create.query &&
          !create.query.destination
        ) {
          create = {
            ...create,
            query: { ...create.query, destination: pickedDestination.value },
          };
        }
        // Default types (task/event/page — including the no-`as` default
        // Task) apply their pill values through the typed defaults payload;
        // custom types through generic graph properties.
        if (selectedDefaultKind.value) {
          const defaults = collectedDefaults();
          emit("submit", defaults ? { ...create, defaults } : create);
          return;
        }
        const properties = collectedProperties();
        if (Object.keys(properties).length > 0) {
          emit("submit", { ...create, properties });
          return;
        }
        emit("submit", create);
        return;
      }
      emit("submit", query);
    };
    const placeholder = ref(
      'Enter a title, use <span class="keyword">on</span> to specify a date, <span class="keyword">#</span> for labels, <span class="keyword">!</span> for priorities'
    );
    const fillPlaceholder = (mode: string | undefined) => {
      switch (mode) {
        case "search":
          placeholder.value = "Search…";
          break;
        case "destination":
          placeholder.value =
            "Specify the target object: task, page, event etc";
          break;
        case "schedule":
          placeholder.value = "Specify a day to work on the task";
          break;
        case "postpone":
          placeholder.value = "Specify a week to work on the task";
          break;
        default:
          break;
      }
    };
    const fill = async (state: ParsedInputState) => {
      currentMode.value = state.mode;
      fillPlaceholder(state.mode);
      await parsedInput.value?.fill(state);
    };
    const focus = () => {
      parsedInput.value?.focus();
    };
    const submit = (isOpeningItem = false, withContext?: boolean) => {
      parsedInput.value?.submit(isOpeningItem, true, withContext);
    };
    const selectCompletion = () => {
      parsedInput.value?.openObject();
    };
    const rebuildParser = () => {
      parsedInput.value?.rebuildParser();
    };
    const navigateInCompletions = (direction: "up" | "down") => {
      if (direction === "up") parsedInput.value?.navigateUp();
      else parsedInput.value?.navigateDown();
    };
    // Full reset, called by the shell when the console closes (escape,
    // background click, submit). Clearing the input alone is not enough: the
    // type-change event is deduped, so when the type is already the default
    // Task no event fires and the pill state would survive to the next open.
    const clear = () => {
      currentMode.value = undefined;
      propertyValues.value = {};
      pickedDestination.value = null;
      mandate.value = null;
      parsedType.value = null;
      applySelectedType();
      parsedInput.value?.clear(true);
      // Dismiss any picker opened from a pill: the console can be cleared from
      // the outside (submit, escape, background click) while one is showing,
      // and it would otherwise survive the reset and stay on screen.
      if (datePickerEl.value?.isDisplayed) datePickerEl.value.closePicker();
      if (destinationPickerEl.value?.isDisplayed)
        destinationPickerEl.value.closePicker();
      if (typePickerEl.value?.isDisplayed) typePickerEl.value.closePicker();
    };
    const onEscape = () => {
      if (props.modal) {
        emit("close");
      }
    };
    return {
      clear,
      destination,
      fill,
      focus,
      focused,
      isGenericMode,
      navigateInCompletions,
      onClearPropertyPill,
      onSelectPropertyPill,
      onSelectDestinationPill,
      onClearDestinationPill,
      onMandateChange,
      onSelectTypePill,
      onSubmit,
      onTypeChange,
      onUpdateSelectPill,
      onUpdateRelationPill,
      onUpdateBoolPill,
      onUpdateTextPill,
      onUpdateNumberPill,
      onUpdateNotesPill,
      parsedInput,
      captureAvailability,
      pillContext,
      pillValues,
      placeholder,
      rebuildParser,
      selectCompletion,
      selectedType,
      selectedTypeLabel,
      selectedProperties,
      showDestination,
      submit,
      typePickerEl,
      selectedTypePickerId,
      onEscape,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/console.scss" as *;

.console {
  box-shadow: $console;
  box-sizing: border-box;
  border-radius: $border-radius-l;
  cursor: text;
  display: flex;
  flex-wrap: wrap;
  max-width: 740px;
  padding: $paddingConsole;
  @include themed() {
    background: t($bg);
    caret-color: t($secondary);
  }

  &.with-completions {
    height: fit-content;

    // Without the footer bar (search/schedule/postpone modes) the suggestions
    // are the last element and run flush to the bottom edge.
    &:not(.with-footer) {
      padding-bottom: 0;
    }

    // Scope to the query field (a direct child of `.parsed-input`) so these
    // don't leak onto `.input` elements nested in property pills (e.g. the
    // GraphString text editor renders an `<input class="input">`).
    :deep(.parsed-input > .input) {
      padding-bottom: 14px;
      @include themed() {
        border-bottom: solid 1px t($grey-10);
      }
    }

    :deep(.console-input .parsed-input > .input) {
      padding-bottom: 14px;
    }

    // A hairline setting the property pills apart from the suggestion list
    // below them.
    :deep(.console-pills) {
      padding-bottom: 10px;
      @include themed() {
        border-bottom: solid 1px t($grey-10);
      }
    }

    // The suggestion list pads its own bottom edge, so drop the footer bar's
    // top margin: the last suggestion then sits as close to the footer
    // hairline as the first one does to the pills hairline above.
    :deep(.console-footer-bar) {
      margin-top: 0;
    }
  }

  .console-input {
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    flex-shrink: 0;
    overflow: hidden;
    @extend %font-console-input;
    @include themed() {
      color: t($primary);
    }
    :deep(.parsed-input > .input) {
      padding: 0 $paddingInput;
    }
    :deep(.completions) {
      padding: $paddingCompletions;
    }
    :deep(p.query) {
      max-width: 100%;
      white-space: pre-wrap;
    }
  }
}
</style>
